import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikSelect, { TOption } from "./FormikSelect";
import FormikTextField from "./FormikTextField";
import {
  LESS_THAN,
  LESS_THAN_OR_EQUAL_TO,
  EQUAL_TO,
  GREATER_THAN_OR_EQUAL_TO,
  GREATER_THAN,
} from "./Constants";
import DialogButtons from "./DialogButtons";
import { useTranslation } from "react-i18next";

export { EQUAL_TO };
export type TNumericOperator =
  | typeof LESS_THAN
  | typeof LESS_THAN_OR_EQUAL_TO
  | typeof EQUAL_TO
  | typeof GREATER_THAN_OR_EQUAL_TO
  | typeof GREATER_THAN;

const operatorOptions: TOption<string>[] = [
  { value: LESS_THAN },
  { value: LESS_THAN_OR_EQUAL_TO },
  { value: EQUAL_TO },
  { value: GREATER_THAN_OR_EQUAL_TO },
  { value: GREATER_THAN },
];

interface INumericConditionDialogProps {
  title: string;
  operator: TNumericOperator;
  value: number;
  open: boolean;
  onClose: (operator: TNumericOperator, value: number) => void;
}

const NumericConditionDialog = (props: INumericConditionDialogProps) => {
  const { title, operator, value, open, onClose } = props;
  const { t } = useTranslation();

  return (
    <Dialog open={open} fullWidth data-cy="condition-picker-condition-dialog">
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={{ operator: operator, value: value }}
        validationSchema={Yup.object({
          value: Yup.number().required(t("A number is required")),
        })}
        onSubmit={(values) => onClose(values.operator, values.value)}
      >
        <Form>
          <DialogContent>
            <Box gap={1} display="flex" flexDirection="row">
              <FormikSelect
                label={t("Operator")}
                name="operator"
                options={operatorOptions}
                style={{ width: "40%" }}
                margin="none"
                data-cy="condition-picker-select-operator"
              />
              <FormikTextField
                label={t("Value")}
                name="value"
                type="number"
                style={{ width: "60%" }}
                margin="none"
                data-cy="condition-picker-value"
              />
            </Box>
          </DialogContent>
          <DialogButtons
            submitButtonLabel="Save"
            resetCallback={() => onClose(operator, value)}
          />
        </Form>
      </Formik>
    </Dialog>
  );
};

type TNumericConditionPickerProps = {
  label: string;
  operator: TNumericOperator;
  value: number;
  onConditionChange: (operator: TNumericOperator, value: number) => void;
};

const NumericConditionPicker = (
  props: TNumericConditionPickerProps & TextFieldProps
) => {
  const { label, operator, value, onConditionChange, ...textFieldProps } =
    props;
  const [condition, setCondition] = useState<string>(operator + " " + value);
  const [open, setOpen] = useState<boolean>(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (operator: TNumericOperator, value: number) => {
    setOpen(false);
    setCondition(operator + " " + value.toString());
    onConditionChange(operator, value);
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        label={label}
        value={condition}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleClick}
      />
      <NumericConditionDialog
        title={label}
        operator={operator}
        value={value}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

export default NumericConditionPicker;
